import {
  Dropdown,
  Field,
  makeStyles,
  Option,
  Switch,
  tokens,
} from '@fluentui/react-components';
import { MaskMode, useRecordContext, useT } from '@powerportalspro/react';
import { Section, SectionColumn, TextEdit } from '@powerportalspro/react-fluent';
import { useMemo, useState } from 'react';

// Localization namespace — same shape the Blazor demo's
// `IStringLocalizer<TextEditMaskDemo>` resolves to. Sharing the keys means
// both stacks read the same preset labels and "Stored value:" wording.
const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Editors.TextEdit.TextEditMaskDemo';

// Mirror of the Blazor demo's `MaskPreset` record. Each preset selects a
// MaskMode + the per-mode template/pattern that drives `TextEdit`'s new
// `mask` / `pattern` / `allowedPattern` props.
type MaskPreset = {
  labelKey: string;
  mode: MaskMode;
  pattern?: string;
  allowedPattern?: string;
};

const PRESETS: MaskPreset[] = [
  { labelKey: 'preset.none', mode: MaskMode.None },
  { labelKey: 'preset.phone', mode: MaskMode.Pattern, pattern: '(000) 000-0000' },
  { labelKey: 'preset.ssn', mode: MaskMode.Pattern, pattern: '000-00-0000' },
  { labelKey: 'preset.credit-card', mode: MaskMode.Pattern, pattern: '0000 0000 0000 0000' },
  { labelKey: 'preset.zip', mode: MaskMode.Pattern, pattern: '00000-0000' },
  { labelKey: 'preset.letters', mode: MaskMode.Regex, allowedPattern: '[A-Za-z ]' },
];

const COLUMN_NAME = 'ppp_mobilephone';

const useStyles = makeStyles({
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
  },
  field: {
    // Same auto-auto grid pattern the working `TextEditPropertiesSample` uses
    // for its inline label+input pair. Fluent's horizontal-orientation Field
    // is a CSS grid; overriding `gridTemplateColumns` to `auto auto` makes the
    // label column and the input column size to content (rather than the
    // 1fr/1fr default that stretches the input across whatever flex-basis the
    // Field claimed). With `flexShrink: 0` on the Field and `nowrap` on the
    // label, the Field's outer box ends up exactly `label + gap + dropdown`
    // wide — no more, no less — so the toolbar's row math stays predictable.
    flexShrink: 0,
    gridTemplateColumns: 'auto auto',
    '& label': {
      whiteSpace: 'nowrap',
    },
  },
  dropdown: {
    // `minWidth` (not `width`) so the trigger can still grow if a longer
    // preset label is ever added, but never below ~150px so even "None" /
    // short labels render as a recognizable dropdown rather than a tiny
    // pill. Matches the `typeInput: { minWidth: '125px' }` shape the
    // Properties sample uses on the same row layout.
    minWidth: '150px',
  },
  storedValue: {
    marginTop: tokens.spacingVerticalM,
  },
  code: {
    // Mirror of the Blazor demo's `<code>` styling: subtle background +
    // monospace so the live stored value reads as a value, not prose.
    backgroundColor: tokens.colorNeutralBackground2,
    padding: `0 ${tokens.spacingHorizontalXS}`,
    borderRadius: tokens.borderRadiusSmall,
    fontFamily: tokens.fontFamilyMonospace,
  },
});

export function TextEditMaskSample() {
  const styles = useStyles();
  const t = useT();
  const { record } = useRecordContext();

  // Default to "Phone" so the masking is visible on first load — same as the
  // Blazor demo's `_selected = _presets.Skip(1).First()`.
  const [presetIndex, setPresetIndex] = useState(1);
  const [storeMasked, setStoreMasked] = useState(false);

  const presetOptions = useMemo(
    () =>
      PRESETS.map((p, i) => ({
        index: i,
        label: t(`${STRINGS_BASE}.${p.labelKey}`),
      })),
    [t],
  );

  const selected = PRESETS[presetIndex] ?? PRESETS[0]!;
  const selectedLabel = presetOptions[presetIndex]?.label ?? '';

  // Live readout of what the bound column actually holds, same as the Blazor
  // demo's `_stored` updated via `ValueChanged`. Reading directly from the
  // record (via the live RecordContext) avoids a parallel `onValueChange`
  // subscription on TextEdit — the binding flow already keeps this fresh.
  const storedValue = record?.properties?.[COLUMN_NAME]?.value;
  const storedDisplay =
    typeof storedValue === 'string' && storedValue.length > 0 ? storedValue : '—';

  return (
    <Section>
      <SectionColumn
        header={
          <div className={styles.toolbar}>
            <Field
              className={styles.field}
              label={t(`${STRINGS_BASE}.mask`)}
              orientation="horizontal"
            >
              <Dropdown
                className={styles.dropdown}
                value={selectedLabel}
                selectedOptions={[String(presetIndex)]}
                onOptionSelect={(_, data) => {
                  const next = Number(data.optionValue);
                  if (!Number.isNaN(next)) setPresetIndex(next);
                }}
              >
                {presetOptions.map((opt) => (
                  <Option key={opt.index} value={String(opt.index)}>
                    {opt.label}
                  </Option>
                ))}
              </Dropdown>
            </Field>
            <Switch
              checked={storeMasked}
              onChange={(_, data) => setStoreMasked(data.checked)}
              label={t(`${STRINGS_BASE}.store-masked`)}
            />
          </div>
        }
      >
        {/*
          `key` on TextEdit forces a fresh mount when the preset changes —
          mirrors Blazor's `@key="_selected?.Label"` rationale. Without it,
          flipping the mask mode while the field is focused / dirty could
          leave caret state from the previous preset stuck in place.
        */}
        <div style={{ maxWidth: '320px' }}>
          <TextEdit
            key={selected.labelKey}
            columnName={COLUMN_NAME}
            label={t(`${STRINGS_BASE}.field-label`)}
            mask={selected.mode}
            pattern={selected.pattern}
            allowedPattern={selected.allowedPattern}
            storeMaskedValue={storeMasked}
            immediate
          />
        </div>

        <p className={styles.storedValue}>
          {t(`${STRINGS_BASE}.stored-value`)}: <code className={styles.code}>{storedDisplay}</code>
        </p>
      </SectionColumn>
    </Section>
  );
}
